// The recipe kit every family kit shares (R924, R1007, R1120, R1125).
//
// The combat, effect, gather, anchor, hold-fire, tube reload and detonation
// kits each hand their recipes one context and one set of primitives, and
// the primitives differed by the ceiling of the family's `[limits]` table
// alone. This module holds that arithmetic once: the full turn, the clamps,
// the eases, the ink mix, the inks the families share, the cell keys and the
// cell scale, the sink primitives as factories that take a family's limits,
// and the look registry the combat, gather, anchor, hold-fire, reload and
// detonation registries build from.
//
// A family kit binds each factory to its own limits when it evaluates, so a
// recipe calls `disc(c, …)` as before and the frame path pays no lookup: what
// a factory returns reads its arguments and the limits object it was given.
// A layer of `fast/layers/` reads the clamps and the eases here, and never a
// factory.
//
// This is a leaf (`leaves.test.ts`). It names the sinks of a layer through a
// type-only clause. **Nothing here allocates on the frame path**: a factory
// runs once per kit, when the kit's module evaluates.

import type {
  CircleSink, QuadSink, SegmentSink, SpriteEntry,
} from '../fast/layers/batches';
import { PALETTE_TOKENS } from '../gen/presentation';
import { packChannels, packHex } from './color';
import { TAU, clamp01 } from './math';

/** A full turn, and the 0..1 clamp of every mockup page (`render/math.ts`).
 *  Every family kit passes them on, so a recipe takes them from its own kit. */
export { TAU, clamp01 };

/** A point in world px. A recipe reads one and keeps no reference. */
export interface Pt { x: number; y: number }

/** What every sink primitive reads off a family context. */
export interface SinkCtx {
  /** A fade over the whole mark. Every alpha is multiplied by it. */
  alpha: number;
  /** The engine's additive-blend flag (`fast/layer.ts` `F_ADD`), handed in
   *  so a leaf takes no engine import — the rule `beam-styles.ts` follows. */
  add: number;
  quads: QuadSink;
  segs: SegmentSink;
  rings: CircleSink;
  /** An atlas cell by key. */
  sprite(key: string): SpriteEntry;
}

/** The two alpha ceilings of one family's `[limits]` table. */
export interface AlphaLimits {
  readonly maxAlphaFill: number;
  readonly maxAlphaStroke: number;
}

/** The atlas key of the one flat texel, for a rectangle under a rotation. */
export const SOLID_KEY = 'white';

/**
 * The atlas keys of a family's baked cells, `<family>:<cell>`, built once
 * when the kit evaluates. A recipe asks for a cell every frame, and a key
 * built a call would be a fresh string for the atlas to hash each time.
 */
export function cellKeys<C extends string>(
  family: string, cells: readonly C[],
): Readonly<Record<C, string>> {
  const out = {} as Record<C, string>;
  for (const cell of cells) out[cell] = `${family}:${cell}`;
  return out;
}

// ---------------------------------------------------------------------------
// The inks the families share
// ---------------------------------------------------------------------------
//
// The canvas is never themed (R1007), so a look wears the inks of
// `ui-map-interface.md` §2 beside the colours its context carries. Each
// family kit passes on the ones its cards name.

/** The pale water: a trail, a wash, silt, a bubble, a work lamp. It is the
 *  chrome `--text` of the palette (R1534). */
export const PALE_INK = packHex(PALETTE_TOKENS['--text']);
/** The deep ink of the sea floor: a shadow on the ground, a hole, a mine.
 *  It is the chrome `--deep` of the palette, packed from its channels. */
export const DEEP_INK = packChannels(PALETTE_TOKENS['--deep-rgb']);
/** The water itself: the core of a hollow piece that must read as empty.
 *  It is the cards' `WATER`. */
export const WATER_INK = 0x0a2739;

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/** `v` held to `lo`..`hi`. The layers read it where a bound is not 0..1. */
export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Straight between two figures. It is `lerp`. */
export function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

/**
 * Two packed inks mixed, `u` of the way from `a` to `b`, `u` clamped: each
 * channel cut to a whole number toward 0. The integer arithmetic allocates
 * nothing. It is the ink mix of the shield card DRAIN SHIFT and of the heat
 * ramp of a round.
 */
export function mixInk(a: number, b: number, u: number): number {
  const v = clamp01(u);
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const rr = (ar + (br - ar) * v) | 0;
  const gg = (ag + (bg - ag) * v) | 0;
  const bl = (ab + (bb - ab) * v) | 0;
  return (rr << 16) | (gg << 8) | bl;
}

/** The fractional part, which is every walking clock of a recipe. */
export function frac(v: number): number {
  return v - Math.floor(v);
}

/** Smoothstep, slow at both ends. It is `ease` of the pages. */
export function ease(v: number): number {
  const u = clamp01(v);
  return u * u * (3 - 2 * u);
}

/** 0 before `a`, 1 after `b`, a smoothstep between: `ease` of the share,
 *  the `smooth` of the pages. */
export function smooth(a: number, b: number, v: number): number {
  return ease((v - a) / (b - a));
}

/** A cubic that starts fast and settles, clamped: `easeOut` and `outCubic`
 *  of the pages. */
export function outCubic(v: number): number {
  const u = 1 - clamp01(v);
  return 1 - u * u * u;
}

/** A cubic that starts slow and ends fast, clamped: `inCubic` of the
 *  pages. */
export function inCubic(u: number): number {
  const v = clamp01(u);
  return v * v * v;
}

/** One axis of the quadratic curve `a`, `c`, `b` at `u`: a card's
 *  `quadraticCurveTo` walked as straight pieces. */
export function bezier2(a: number, c: number, b: number, u: number): number {
  const k = 1 - u;
  return k * k * a + 2 * k * u * c + u * u * b;
}

/** The quad scale that draws a square cell at radius `r`, whatever side the
 *  cell was baked at, so a figure change bakes nothing new. */
export function cellScale(e: SpriteEntry, r: number): number {
  return e.w > 0 ? (2 * r) / e.w : 1;
}

// ---------------------------------------------------------------------------
// The clamps
// ---------------------------------------------------------------------------

function fillOf(lim: AlphaLimits, a: number): number {
  return Math.min(lim.maxAlphaFill, Math.max(0, a));
}

function strokeOf(lim: AlphaLimits, a: number): number {
  return Math.min(lim.maxAlphaStroke, Math.max(0, a));
}

/** A fill alpha clamp, held under the ceiling the limits state. */
export function fillFor(lim: AlphaLimits): (a: number) => number {
  return (a) => fillOf(lim, a);
}

/** A stroke alpha clamp, held under the ceiling the limits state. */
export function strokeFor(lim: AlphaLimits): (a: number) => number {
  return (a) => strokeOf(lim, a);
}

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// Each one clamps its alpha, multiplies by the context fade and skips a mark
// that would draw nothing, so a recipe states the card's figure and never
// guards it.

/** A hard filled circle. */
export function discFor<C extends SinkCtx>(lim: AlphaLimits) {
  return (c: C, x: number, y: number, r: number, ink: number, a: number): void => {
    if (r <= 0) return;
    const al = fillOf(lim, a) * c.alpha;
    if (al <= 0) return;
    c.rings.disc(x, y, r, ink, al);
  };
}

/** A stroked full circle. */
export function ringFor<C extends SinkCtx>(lim: AlphaLimits) {
  return (
    c: C, x: number, y: number, r: number, w: number, ink: number, a: number,
  ): void => {
    if (r <= 0 || w <= 0) return;
    const al = strokeOf(lim, a) * c.alpha;
    if (al <= 0) return;
    c.rings.ring(x, y, r, w, ink, al);
  };
}

/** A stroked partial circle, from `start` over `sweep` radians. A card's
 *  `arc` takes a start and an END, so a port passes `a1 - a0`. */
export function arcFor<C extends SinkCtx>(lim: AlphaLimits) {
  return (
    c: C, x: number, y: number, r: number, w: number,
    start: number, sweep: number, ink: number, a: number,
  ): void => {
    if (r <= 0 || w <= 0 || sweep === 0) return;
    const al = strokeOf(lim, a) * c.alpha;
    if (al <= 0) return;
    c.rings.arc(x, y, r, w, start, sweep, ink, al);
  };
}

/** A dashed ring: `n` dashes, each covering `duty` of its own step. */
export function dashedRingFor<C extends SinkCtx>(lim: AlphaLimits) {
  return (
    c: C, x: number, y: number, r: number, w: number,
    n: number, duty: number, ink: number, a: number,
    start = 0, sweep = TAU,
  ): void => {
    if (r <= 0 || w <= 0 || n < 1) return;
    const al = strokeOf(lim, a) * c.alpha;
    if (al <= 0) return;
    c.rings.dashed(x, y, r, w, n, duty, ink, al, start, sweep);
  };
}

/** A round-capped segment, as `lineFor` returns it. */
export type LineFn<C> = (
  c: C, x0: number, y0: number, x1: number, y1: number,
  w: number, ink: number, a: number, dashOn?: number, dashOff?: number,
) => void;

/**
 * A round-capped segment.
 *
 * `dashOn` and `dashOff` are the dash pattern in world px; a pattern that is
 * not wanted is left out. The segment sink has no dash PHASE, so a card that
 * animates the phase walks the dashes itself with a polyline.
 */
export function lineFor<C extends SinkCtx>(lim: AlphaLimits): LineFn<C> {
  return (c, x0, y0, x1, y1, w, ink, a, dashOn = 0, dashOff = 0) => {
    if (w <= 0) return;
    const al = strokeOf(lim, a) * c.alpha;
    if (al <= 0) return;
    c.segs.line(x0, y0, x1, y1, w, ink, al, 0, dashOn, dashOff);
  };
}

/**
 * A polyline of `n` points read out of `xs` and `ys`: `n − 1` segments, and
 * one more back to the start when `close` asks. The two arrays are the
 * caller's own scratch, so nothing is allocated.
 */
export function polylineFor<C extends SinkCtx>(line: LineFn<C>) {
  return (
    c: C, xs: ArrayLike<number>, ys: ArrayLike<number>, n: number,
    w: number, ink: number, a: number, close = false,
  ): void => {
    for (let i = 1; i < n; i++) {
      line(c, xs[i - 1], ys[i - 1], xs[i], ys[i], w, ink, a);
    }
    if (close && n > 2) line(c, xs[n - 1], ys[n - 1], xs[0], ys[0], w, ink, a);
  };
}

/** A flat rectangle of `w` by `h`, centred on the point and turned to
 *  `rot`: the one flat texel under a rotation. It is `fillRect` under a
 *  rotate. */
export function rectFor<C extends SinkCtx>(lim: AlphaLimits) {
  return (
    c: C, x: number, y: number, w: number, h: number,
    rot: number, ink: number, a: number,
  ): void => {
    if (w <= 0 || h <= 0) return;
    const al = fillOf(lim, a) * c.alpha;
    if (al <= 0) return;
    const e = c.sprite(SOLID_KEY);
    c.quads.quad(x, y, 0, 0, w, h, rot, 0, ink, al, e.u0, e.v0, e.u1, e.v1);
  };
}

/**
 * One soft additive cell of the atlas, scaled to the radius `r`: a baked
 * radial ramp the quad sink tints. `k` scales the quad past the radius, for
 * a ring cell whose picture stands outside the radius a caller names.
 */
export function softFor<C extends SinkCtx>(lim: AlphaLimits, key: string, k = 1) {
  return (c: C, x: number, y: number, r: number, ink: number, a: number): void => {
    if (r <= 0) return;
    const al = fillOf(lim, a) * c.alpha;
    if (al <= 0) return;
    const e = c.sprite(key);
    c.quads.sprite(x, y, 0, e, ink, al, cellScale(e, r * k), c.add);
  };
}

/**
 * A filled cell with its APEX at `(x, y)`, turned to `ang`, `len` long and
 * `hw` half wide at its base: the dart of a shot and the wedge of a cut.
 *
 * The cell is baked with its apex at x = 0 and its base at x = −`natLen`, so
 * the box centre stands `len/2` behind the apex. `ox`/`oy` turn with the
 * quad, so the offset is written in the cell's OWN frame.
 */
export function apexFor<C extends SinkCtx>(lim: AlphaLimits, key: string, natLen: number) {
  return (
    c: C, x: number, y: number, ang: number,
    len: number, hw: number, ink: number, a: number,
  ): void => {
    if (len <= 0 || hw <= 0) return;
    const al = fillOf(lim, a) * c.alpha;
    if (al <= 0) return;
    const e = c.sprite(key);
    const sx = len / natLen, sy = 2 * hw;
    c.quads.quad(
      x, y, e.ox * sx, e.oy * sy, e.w * sx, e.h * sy, ang, 0, ink, al,
      e.u0, e.v0, e.u1, e.v1,
    );
  };
}

// ---------------------------------------------------------------------------
// The look registry
// ---------------------------------------------------------------------------
//
// A look registry turns a World theme look id into the draw code and the
// figure row. The recipes of one field come out of one or more recipe files,
// each of which names the cards it BUILT and the cards it left PENDING; a
// pending card, and any id with no recipe or no row, draws the AUTO look of
// the list, which is position 0 (R822). The registries build their tables
// once, when their modules evaluate, so a frame looks a pick up and does no
// work.

/** What one recipe file says: the built cards and the pending ones. */
export interface RecipeFile<F> {
  readonly recipes: Readonly<Record<string, F>>;
  readonly pending: readonly string[];
}

/** One resolved look: the id asked for, its figures and its draw code. */
export interface Look<R, F> {
  /** The id the player picked. */
  readonly id: string;
  /** Which id actually draws: `id`, or the AUTO look for a pending card. */
  readonly drawn: string;
  /** The figure row of the look that DRAWS. */
  readonly row: R;
  readonly recipe: F;
}

/** Every recipe of every file, by id. */
function mergeRecipes<F>(files: readonly RecipeFile<F>[]): Readonly<Record<string, F>> {
  const out: Record<string, F> = {};
  for (const f of files) for (const [id, r] of Object.entries(f.recipes)) out[id] = r;
  return out;
}

/**
 * Every id of the list, resolved.
 *
 * A look draws itself where it has a recipe AND a figure row. Any other look
 * draws the AUTO look, which is position 0 of the list, with the AUTO row.
 */
export function buildLooks<R, F>(
  ids: readonly string[], recipes: Readonly<Record<string, F>>,
  rows: Readonly<Record<string, R>>,
): Readonly<Record<string, Look<R, F>>> {
  const table: Record<string, Look<R, F>> = {};
  for (const id of ids) {
    const built = recipes[id] !== undefined && rows[id] !== undefined;
    const drawn = built ? id : ids[0];
    table[id] = { id, drawn, row: rows[drawn], recipe: recipes[drawn] };
  }
  return table;
}

/** Which ids one recipe file has built and which it left pending. */
export interface FileHalf {
  recipes: readonly string[];
  pending: readonly string[];
}

/** The registry of one World field whose looks come out of recipe files. */
export interface LookRegistry<R, F> {
  /** The look of one id, or the AUTO look for an id this build does not
   *  hold. */
  readonly look: (id: string) => Look<R, F>;
  /** Which ids each recipe file has built, and which it left pending. The
   *  test of a field reads this back, so an id in neither table fails the
   *  gate rather than resolving to the AUTO look in silence. */
  readonly fileHalves: () => readonly FileHalf[];
}

/**
 * The registry of one field: every id of the list resolved once, when the
 * styles module evaluates, so a frame looks a pick up and does no work.
 */
export function lookRegistry<R, F>(
  ids: readonly string[], files: readonly RecipeFile<F>[],
  rows: Readonly<Record<string, R>>,
): LookRegistry<R, F> {
  const table = buildLooks(ids, mergeRecipes(files), rows);
  const auto = table[ids[0]];
  return {
    look: (id) => table[id] ?? auto,
    fileHalves: () => files.map((f) => ({ recipes: Object.keys(f.recipes), pending: f.pending })),
  };
}
